import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState } from 'react';
import { X, AlertCircle, Bell } from 'lucide-react';
import Modal from '../ui/Modal';

interface RefundInfo {
  refund_amount: number;
  refund_type: string;
  refund_policy: string;
  refund_timeline: string;
  gateway_fee?: number;
  venue_gateway_fee_cost?: number;
  is_split_payment?: boolean;
  split_refund_given?: boolean | null;
  vendor_deduction?: number;
  venue_share?: number;
  platform_commission?: number;
  has_commission?: boolean;
  original_amount?: number;
  refund_breakdown?: {
    total_sessions?: number;
    completed_sessions?: number;
    remaining_sessions?: number;
    original_price?: number;
    refund_percentage?: number;
    session_value?: number;
  };
}

export interface ManualRefundData {
  refund_type: 'none' | 'partial' | 'full';
  refund_amount?: number;
}

interface CancellationReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notifyCustomer: boolean, manualRefund?: ManualRefundData, splitRefundData?: { type: 'none' | 'partial' | 'full'; amount?: number }) => Promise<void>;
  title: string;
  bookingType: 'session' | 'future_sessions' | 'entire_booking';
  bookingInfo?: {
    reference?: string;
    customer_name?: string;
    customer_email?: string;
    sessions_count?: number;
    source?: 'spotts' | 'manual';
    final_price?: number;
  };
  refundInfo?: RefundInfo;
}

const CancellationReasonModal: React.FC<CancellationReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  bookingType,
  bookingInfo,
  refundInfo,
}) => {
  const [reason, setReason] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Manual booking refund state
  const [refundType, setRefundType] = useState<'none' | 'partial' | 'full'>('none');
  const [refundAmount, setRefundAmount] = useState<number | undefined>(undefined);

  // Split payment refund intent
  const [splitRefundType, setSplitRefundType] = useState<'none' | 'partial' | 'full'>('none');
  const [splitRefundAmount, setSplitRefundAmount] = useState<number | undefined>(undefined);

  // Check if this is a manual booking
  const isManualBooking = bookingInfo?.source === 'manual';
  const maxRefund = Number(bookingInfo?.final_price) || 0;
  const isSplitPayment = !!refundInfo?.is_split_payment;

  // Max refund for split payment = what the vendor received (venue_share / refund_amount)
  const splitMaxRefund = Number(refundInfo?.original_amount || 0);

  // Validation for manual booking partial refund
  const partialRefundError = refundType === 'partial'
    ? (!refundAmount || refundAmount <= 0)
      ? 'Enter a valid refund amount'
      : refundAmount > maxRefund
        ? `Cannot exceed booking price of ${CURRENCY_SYMBOL}${maxRefund.toLocaleString()}`
        : null
    : null;

  const isPartialInvalid = refundType === 'partial' && (
    !refundAmount || refundAmount <= 0 || refundAmount > maxRefund
  );

  // Validation for split payment partial refund
  const splitPartialError = splitRefundType === 'partial'
    ? (!splitRefundAmount || splitRefundAmount <= 0)
      ? 'Enter a valid refund amount'
      : splitRefundAmount > splitMaxRefund
        ? `Cannot exceed ${CURRENCY_SYMBOL}${splitMaxRefund.toLocaleString()}`
        : null
    : null;

  const isSplitPartialInvalid = splitRefundType === 'partial' && (
    !splitRefundAmount || splitRefundAmount <= 0 || splitRefundAmount > splitMaxRefund
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason.trim()) {
      setError('Please provide a reason for cancellation');
      return;
    }

    if (isManualBooking && isPartialInvalid) {
      setError('Please enter a valid refund amount');
      return;
    }

    if (isSplitPayment && isSplitPartialInvalid) {
      setError('Please enter a valid refund amount');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const manualRefund: ManualRefundData | undefined = isManualBooking
        ? {
            refund_type: refundType,
            refund_amount: refundType === 'partial' ? refundAmount : undefined,
          }
        : undefined;

      const splitRefundData = isSplitPayment
        ? {
            type: splitRefundType,
            amount: splitRefundType === 'partial'
              ? splitRefundAmount
              : splitRefundType === 'full'
                ? (refundInfo?.refund_amount || 0)
                : 0,
          }
        : undefined;

      await onConfirm(reason, notifyCustomer, manualRefund, splitRefundData);
      // Reset form state on success
      setReason('');
      setNotifyCustomer(false);
      setRefundType('none');
      setRefundAmount(undefined);
      setSplitRefundType('none');
      setSplitRefundAmount(undefined);
      // Wait a brief moment for parent to show toast, then close modal
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getWarningMessage = () => {
    if (isManualBooking) {
      return 'This will cancel the manual booking and free up the time slot.';
    }

    switch (bookingType) {
      case 'session':
        return 'This will cancel a single session.';
      case 'future_sessions':
        return `This will cancel ${bookingInfo?.sessions_count || 'all'} future session(s).`;
      case 'entire_booking':
        return 'This will cancel the ENTIRE recurring booking plan.';
      default:
        return 'This action cannot be undone.';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" animate={false}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">{title}</h2>
        <button
          onClick={onClose}
          className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Body */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Warning Message */}
            <div className={`flex items-start gap-2 sm:gap-3 p-3 sm:p-4 border rounded-xl sm:rounded-lg ${
              isManualBooking
                ? 'bg-green-50 border-green-200'
                : 'bg-orange-50 border-orange-200'
            }`}>
              <AlertCircle className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 ${
                isManualBooking ? 'text-green-600' : 'text-orange-600'
              }`} />
              <div>
                <p className={`text-sm sm:text-base font-medium ${
                  isManualBooking ? 'text-green-900' : 'text-orange-900'
                }`}>
                  {isManualBooking ? 'Manual Booking Cancellation' : 'Important'}
                </p>
                <p className={`text-xs sm:text-sm mt-1 ${
                  isManualBooking ? 'text-green-700' : 'text-orange-700'
                }`}>
                  {getWarningMessage()}
                </p>
                {bookingInfo && (
                  <div className={`mt-2 text-xs sm:text-sm ${
                    isManualBooking ? 'text-green-700' : 'text-orange-700'
                  }`}>
                    <p><strong>Reference:</strong> {bookingInfo.reference}</p>
                    <p><strong>Customer:</strong> {bookingInfo.customer_name}</p>
                    {isManualBooking && maxRefund > 0 && (
                      <p><strong>Amount Paid:</strong> {CURRENCY_SYMBOL}{maxRefund.toLocaleString()}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* SPLIT PAYMENT: money already in vendor's bank — vendor decides refund */}
            {!isManualBooking && isSplitPayment && refundInfo && (
              <div className="p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-xl sm:rounded-lg space-y-3">
                <h3 className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Split Payment Booking</span>
                </h3>
                <div className="space-y-3 text-xs sm:text-sm">
                  {/* Amount summary */}
                  <div className="bg-white rounded-lg p-2 sm:p-3 space-y-1.5 text-gray-700">
                    <div className="flex justify-between">
                      <span>Customer paid:</span>
                      <span className="font-semibold">{CURRENCY_SYMBOL}{Number(refundInfo.original_amount || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>You received (after Paystack fee):</span>
                      <span className="font-semibold text-amber-900">{CURRENCY_SYMBOL}{Number(refundInfo.refund_amount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  <p className="text-xs text-amber-800">
                    Payment was deposited directly to your bank. Spotts will not process any refund automatically.
                    Please select what you will do:
                  </p>
                  {/* Refund intent selection */}
                  <div className="space-y-2">
                    {(['none', 'partial', 'full'] as const).map((type) => (
                      <label key={type} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="split_refund_type"
                          value={type}
                          checked={splitRefundType === type}
                          onChange={() => {
                            setSplitRefundType(type);
                            if (type !== 'partial') setSplitRefundAmount(undefined);
                          }}
                          className="w-4 h-4 text-amber-600 border-amber-400 focus:ring-amber-500"
                          disabled={isSubmitting}
                        />
                        <span className="text-xs sm:text-sm text-gray-800">
                          {type === 'none' && 'No refund (keep full payment — per cancellation policy)'}
                          {type === 'partial' && 'Partial refund (specify amount below)'}
                          {type === 'full' && `Full refund — return ${CURRENCY_SYMBOL}${Number(refundInfo.refund_amount || 0).toLocaleString()} to customer`}
                        </span>
                      </label>
                    ))}
                  </div>
                  {/* Partial amount input */}
                  {splitRefundType === 'partial' && (
                    <div className="mt-2">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Refund Amount ({CURRENCY_SYMBOL}) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={splitRefundAmount != null ? splitRefundAmount.toLocaleString() : ''}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, '');
                          setSplitRefundAmount(raw ? Math.max(0, Number(raw)) : undefined);
                        }}
                        placeholder="Enter refund amount..."
                        disabled={isSubmitting}
                        className={`w-full px-3 py-2 border rounded-lg text-sm outline-none ${
                          splitPartialError
                            ? 'border-red-400 bg-red-50 focus:border-red-500'
                            : 'border-amber-300 bg-white focus:border-amber-500'
                        }`}
                      />
                      {splitPartialError ? (
                        <p className="text-xs text-red-600 font-medium mt-1">{splitPartialError}</p>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">
                          Max: {CURRENCY_SYMBOL}{splitMaxRefund.toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-amber-700 italic">
                    This records your intent for transparency. Please process the actual refund via bank transfer, cash, or POS reversal.
                  </p>
                </div>
              </div>
            )}

            {/* MANUAL PAYOUT: show vendor's actual deduction (their share, not the full price) */}
            {!isManualBooking && !isSplitPayment && refundInfo && refundInfo.refund_amount > 0 && (
              <div className="space-y-3">
                {/* What customer gets back */}
                <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-xl sm:rounded-lg">
                  <h3 className="text-sm sm:text-base font-semibold text-green-900 mb-2 sm:mb-3">💰 Customer Refund</h3>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-700">Customer gets back:</span>
                      <span className="font-semibold text-green-900">{CURRENCY_SYMBOL}{refundInfo.refund_amount.toLocaleString()}</span>
                    </div>
                    {(refundInfo.gateway_fee ?? 0) > 0 && (
                      <div className="flex justify-between text-xs text-green-700">
                        <span>Paystack fee deducted from refund:</span>
                        <span>{CURRENCY_SYMBOL}{Number(refundInfo.gateway_fee).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-green-200">
                      <p className="text-green-700 italic text-xs">Vendor cancellations always give the customer a full refund.</p>
                    </div>
                  </div>
                </div>

                {/* What gets deducted from vendor's balance */}
                <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl sm:rounded-lg">
                  <h3 className="text-xs sm:text-sm font-semibold text-red-900 mb-2 flex items-center gap-1.5 sm:gap-2">
                    <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="leading-tight">Deducted from Your Balance</span>
                  </h3>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="bg-white rounded-lg p-2 sm:p-3 space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Customer paid:</span>
                        <span className="font-semibold">{CURRENCY_SYMBOL}{Number(refundInfo.original_amount || 0).toLocaleString()}</span>
                      </div>
                      {refundInfo.has_commission && (
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Spotts commission:</span>
                          <span>{CURRENCY_SYMBOL}{Number(refundInfo.platform_commission || 0).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-1 border-t border-gray-100 font-bold text-red-800">
                        <span>Deducted from your balance:</span>
                        <span>{CURRENCY_SYMBOL}{Number(refundInfo.vendor_deduction || refundInfo.venue_share || refundInfo.refund_amount || 0).toLocaleString('en-NG')}</span>
                      </div>
                    </div>
                    <p className="text-xs text-red-700">
                      Only your share is deducted — Spotts commission is not charged to your balance.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Reason Input */}
            <div>
              <label htmlFor="reason" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Reason for Cancellation <span className="text-red-500">*</span>
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError('');
                }}
                className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-xl sm:rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows={3}
                placeholder="Please explain why you are cancelling this booking..."
                disabled={isSubmitting}
                required
              />
            </div>

            {/* Manual Booking Refund Tracking */}
            {isManualBooking && maxRefund > 0 && (
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Refund Status <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {(['none', 'partial', 'full'] as const).map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="manual_refund_type"
                        value={type}
                        checked={refundType === type}
                        onChange={() => {
                          setRefundType(type);
                          if (type !== 'partial') setRefundAmount(undefined);
                          setError('');
                        }}
                        className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                        disabled={isSubmitting}
                      />
                      <span className="text-xs sm:text-sm text-gray-700">
                        {type === 'none' && 'No refund (keep 100% earnings)'}
                        {type === 'partial' && 'Partial refund (specify amount below)'}
                        {type === 'full' && `Full refund (return ${CURRENCY_SYMBOL}${maxRefund.toLocaleString()})`}
                      </span>
                    </label>
                  ))}
                </div>

                {/* Partial Refund Amount Input */}
                {refundType === 'partial' && (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Refund Amount ({CURRENCY_SYMBOL}) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={refundAmount != null ? refundAmount.toLocaleString() : ''}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, '');
                        setRefundAmount(raw ? Math.max(0, Number(raw)) : undefined);
                        setError('');
                      }}
                      placeholder="Enter refund amount..."
                      disabled={isSubmitting}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none ${
                        partialRefundError
                          ? 'border-red-400 bg-red-50 focus:border-red-500'
                          : 'border-gray-200 focus:border-blue-400'
                      }`}
                    />
                    {partialRefundError ? (
                      <p className="text-xs text-red-600 font-medium mt-1">
                        {partialRefundError}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        Max refund: {CURRENCY_SYMBOL}{maxRefund.toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Refund info note */}
                <p className="text-xs text-gray-500 mt-3 italic">
                  This records the refund for revenue tracking. The actual refund is handled externally (cash, transfer, or POS reversal).
                </p>
              </div>
            )}

            {error && (
              <p className="text-xs sm:text-sm text-red-600">{error}</p>
            )}

            {/* Customer Notification — optional, off by default */}
            {bookingInfo?.customer_email ? (
              <label className="flex items-start gap-3 cursor-pointer select-none p-3 sm:p-4 bg-blue-50 rounded-xl sm:rounded-lg border border-blue-200">
                <input
                  type="checkbox"
                  checked={notifyCustomer}
                  onChange={(e) => setNotifyCustomer(e.target.checked)}
                  disabled={isSubmitting}
                  className="mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer shrink-0"
                />
                <div>
                  <span className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-gray-900">
                    <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 shrink-0" />
                    Notify customer by email
                  </span>
                  <span className="block text-xs text-gray-600 mt-0.5">
                    Send a cancellation email to {bookingInfo.customer_email}
                  </span>
                </div>
              </label>
            ) : (
              <div className="flex items-start gap-3 p-3 sm:p-4 bg-gray-50 rounded-xl sm:rounded-lg border border-gray-200">
                <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 shrink-0 mt-0.5" />
                <div>
                  <span className="text-xs sm:text-sm font-medium text-gray-500">Customer notification not available</span>
                  <span className="block text-xs text-gray-400 mt-0.5">No email on file for this customer</span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl sm:rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 bg-red-600 text-white rounded-xl sm:rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-2"
              disabled={isSubmitting || !reason.trim() || (isManualBooking && isPartialInvalid)}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Cancelling...
                </>
              ) : 'Confirm Cancellation'}
            </button>
          </div>
        </form>
        </div>
    </Modal>
  );
};

export default CancellationReasonModal;
